import React, { useState, useEffect } from 'react';
import { boatService } from '../services/boatService';
import type { BoatInfo, HullType, SaveBoatData } from '../types';

/**
 * Page for viewing and editing boat information.
 * Requirements: Boat Info 2, 3, 4, 5
 */
const BoatInfoPage: React.FC = () => {
  const [boat, setBoat] = useState<BoatInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [boatName, setBoatName] = useState('');
  const [hullType, setHullType] = useState<HullType>('Monohull');
  const [lengthMeters, setLengthMeters] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadBoat();
  }, []);

  const loadBoat = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await boatService.getBoat();
      setBoat(data);
      if (data) {
        setBoatName(data.boatName);
        setHullType(data.hullType);
        setLengthMeters(data.lengthMeters.toString());
      }
    } catch (err: any) {
      setError('Failed to load boat information');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!boatName.trim()) {
      errors.boatName = 'Boat name is required';
    } else if (boatName.length > 100) {
      errors.boatName = 'Boat name must not exceed 100 characters';
    }
    
    if (!hullType || (hullType !== 'Monohull' && hullType !== 'Multihull')) {
      errors.hullType = "Hull type must be 'Monohull' or 'Multihull'";
    }
    
    const length = parseFloat(lengthMeters);
    if (!lengthMeters || isNaN(length)) {
      errors.lengthMeters = 'Length is required';
    } else if (length < 1 || length > 150) {
      errors.lengthMeters = 'Length must be between 1 and 150 meters';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    try {
      setSaving(true);
      setError(null);
      const data: SaveBoatData = {
        boatName: boatName.trim(),
        hullType,
        lengthMeters: parseFloat(lengthMeters),
      };
      const saved = await boatService.saveBoat(data);
      setBoat(saved);
      setEditing(false);
    } catch (err: any) {
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      } else {
        setError(err.response?.data?.message || 'Failed to save boat information');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (boat) {
      setBoatName(boat.boatName);
      setHullType(boat.hullType);
      setLengthMeters(boat.lengthMeters.toString());
    } else {
      setBoatName('');
      setHullType('Monohull');
      setLengthMeters('');
    }
    setValidationErrors({});
    setEditing(false);
  };

  const handleEdit = () => {
    setEditing(true);
    setValidationErrors({});
  };

  if (loading) {
    return (
      <div className="boat-info-page">
        <h1>Boat Information</h1>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="boat-info-page">
      <h1>Boat Information</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      {!boat && !editing ? (
        <div className="boat-empty-state">
          <p>No boat information saved yet.</p>
          <button onClick={() => setEditing(true)} className="primary-button">
            Add Boat Info
          </button>
        </div>
      ) : editing ? (
        <div className="boat-form">
          <div className="form-group">
            <label htmlFor="boatName">Boat Name</label>
            <input
              id="boatName"
              type="text"
              value={boatName}
              onChange={(e) => setBoatName(e.target.value)}
              maxLength={100}
              disabled={saving}
            />
            {validationErrors.boatName && (
              <span className="field-error">{validationErrors.boatName}</span>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="hullType">Hull Type</label>
            <select
              id="hullType"
              value={hullType}
              onChange={(e) => setHullType(e.target.value as HullType)}
              disabled={saving}
            >
              <option value="Monohull">Monohull</option>
              <option value="Multihull">Multihull</option>
            </select>
            {validationErrors.hullType && (
              <span className="field-error">{validationErrors.hullType}</span>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="lengthMeters">Length (meters)</label>
            <input
              id="lengthMeters"
              type="number"
              value={lengthMeters}
              onChange={(e) => setLengthMeters(e.target.value)}
              min="1"
              max="150"
              step="0.1"
              disabled={saving}
            />
            {validationErrors.lengthMeters && (
              <span className="field-error">{validationErrors.lengthMeters}</span>
            )}
          </div>
          
          <div className="form-actions">
            <button onClick={handleSave} disabled={saving} className="primary-button">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={handleCancel} disabled={saving} className="secondary-button">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="boat-details">
          <div className="detail-row">
            <span className="detail-label">Boat Name:</span>
            <span className="detail-value">{boat?.boatName}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Hull Type:</span>
            <span className="detail-value">{boat?.hullType}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Length:</span>
            <span className="detail-value">{boat?.lengthMeters} meters</span>
          </div>
          <div className="form-actions">
            <button onClick={handleEdit} className="primary-button">
              Edit
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoatInfoPage;
